'use client';

/**
 * AvailabilityCalendar — month grid that bolds the dates a Bookeasy operator can
 * actually be booked (mirrors the competitor / Bookeasy's own date picker).
 *
 * Availability comes from /api/bookeasy/operators/<id>/availability-calendar,
 * which probes each night via the production RatesGrid (cached server-side). A
 * day is bold + clickable when bookable, struck when full, neutral when unknown.
 * Picking a day calls onPick(dateStr) — the widget turns it into a check-in.
 */
import React, { useEffect, useMemo, useState } from 'react';

const API = 'https://app.trip-planner.com.au/api';

interface DayInfo {
  date: string;
  available: boolean | null;
  price?: number | null;
}

interface Props {
  operatorId: number;
  product: 'accom' | 'tours';
  selected?: string;            // currently chosen check-in (YYYY-MM-DD)
  onPick?: (date: string) => void;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function ymd(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function AvailabilityCalendar({ operatorId, product, selected, onPick }: Props) {
  // Month being viewed — start on the current month.
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [days, setDays] = useState<Record<string, DayInfo>>({});
  const [loading, setLoading] = useState(false);

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadBlanks = firstOfMonth.getDay(); // 0 (Sun) … 6
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  useEffect(() => {
    let cancelled = false;
    const start = ymd(firstOfMonth);
    setLoading(true);
    fetch(`${API}/bookeasy/operators/${operatorId}/availability-calendar?product=${product}&start=${start}&days=${daysInMonth}`)
      .then((r) => (r.ok ? r.json() : { days: [] }))
      .then((j) => {
        if (cancelled) return;
        const map: Record<string, DayInfo> = {};
        for (const d of j.days || []) map[d.date] = d;
        setDays(map);
      })
      .catch(() => { if (!cancelled) setDays({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorId, product, year, month, daysInMonth]);

  const cells: (DayInfo & { dayNum: number; isPast: boolean })[] = [];
  for (let i = 0; i < daysInMonth; i++) {
    const date = ymd(new Date(year, month, i + 1));
    const info = days[date] || { date, available: null };
    const isPast = new Date(year, month, i + 1) < today;
    cells.push({ ...info, dayNum: i + 1, isPast });
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setView(new Date(year, month - 1, 1))}
          disabled={isCurrentMonth}
          aria-label="Previous month"
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <div className="text-sm font-semibold text-slate-900">
          {MONTHS[month]} {year}
          {loading && <span className="ml-2 text-xs font-normal text-slate-400">loading…</span>}
        </div>
        <button
          type="button"
          onClick={() => setView(new Date(year, month + 1, 1))}
          aria-label="Next month"
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((d, i) => (
          <div key={i} className="pb-1 text-[11px] font-semibold uppercase text-slate-400">{d}</div>
        ))}
        {Array.from({ length: leadBlanks }).map((_, i) => <div key={`b${i}`} />)}
        {cells.map((c) => {
          const bookable = c.available === true && !c.isPast;
          const isSelected = selected === c.date;
          const base = 'relative flex h-11 flex-col items-center justify-center rounded-lg text-sm transition';
          let cls: string;
          if (c.isPast) {
            cls = 'text-slate-300';
          } else if (bookable) {
            cls = isSelected
              ? 'bg-emerald-600 text-white font-bold shadow'
              : 'font-bold text-slate-900 ring-1 ring-emerald-200 bg-emerald-50/60 hover:bg-emerald-100 cursor-pointer';
          } else if (c.available === false) {
            cls = 'text-slate-300 line-through';
          } else {
            cls = 'text-slate-400';
          }
          return (
            <button
              key={c.date}
              type="button"
              disabled={!bookable}
              onClick={() => bookable && onPick?.(c.date)}
              title={bookable && typeof c.price === 'number' ? `Available — from $${Math.round(c.price)}` : undefined}
              className={`${base} ${cls}`}
            >
              <span>{c.dayNum}</span>
              {bookable && typeof c.price === 'number' && (
                <span className={`text-[10px] font-medium leading-none ${isSelected ? 'text-emerald-50' : 'text-emerald-600'}`}>
                  ${Math.round(c.price)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded ring-1 ring-emerald-200 bg-emerald-50" /> Available
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-slate-100" /> <span className="line-through">Booked</span>
        </span>
      </div>
    </div>
  );
}
